import { useState } from 'react';
import { Link } from 'react-router-dom';
import { GoogleLogo, AppleLogo, Envelope, Lock, User } from '@phosphor-icons/react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../contexts/AuthContext';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import Input from '../components/ui/Input';

export default function WelcomePage() {
  const { signInWithGoogle, signInWithApple, signInWithEmail, signUpWithEmail, resetPassword } = useAuth();
  const isIOS = Capacitor.getPlatform() === 'ios';
  const [mode, setMode] = useState<'welcome' | 'signin' | 'signup'>('welcome');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleGoogleSignIn = async () => {
    sessionStorage.setItem('explicit_terms_accepted', 'true');
    try {
      setLoading(true);
      setError('');
      await signInWithGoogle();
    } catch (err: any) {
      setError("Couldn't sign in with Google right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    sessionStorage.setItem('explicit_terms_accepted', 'true');
    try {
      setLoading(true);
      setError('');
      await signInWithApple();
    } catch (err: any) {
      setError("Couldn't sign in with Apple right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      if (mode === 'signup') {
        if (!displayName.trim()) {
          setError('Please enter your name');
          return;
        }
        sessionStorage.setItem('explicit_terms_accepted', 'true');
        await signUpWithEmail(email, password, displayName);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err: any) {
      // Show Firebase-specific messages (wrong password, user not found, etc.) but soften generic ones
      const firebaseMsg = err.code ? err.message : undefined;
      setError(
        firebaseMsg || `Couldn't ${mode === 'signup' ? 'create your account' : 'sign you in'} right now. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  const isSignUp = mode === 'signup';
  const keyboardHeight = useKeyboardHeight();

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-orange-100 via-pink-50 to-white dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 flex flex-col items-center justify-center px-6 py-12 overflow-y-auto"
      style={{ paddingBottom: keyboardHeight > 0 ? keyboardHeight + 16 : undefined }}
    >
      {/* Stacked lockup — matches AdminBrand "Stacked — Light" */}
      <div className="flex flex-col items-center gap-3 mb-10">
        <svg width={88} height={88} viewBox="0 0 400 400" fill="none">
          <defs>
            <linearGradient id="wlc-bm" x1="40" y1="360" x2="360" y2="40" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#FF6B9D" />
              <stop offset="100%" stopColor="#FF8C69" />
            </linearGradient>
          </defs>
          <circle cx="200" cy="200" r="190" fill="url(#wlc-bm)" />
          <circle cx="200" cy="159" r="70" fill="white" />
          <path d="M 200 123 L 214 150 L 241 159 L 214 168 L 200 195 L 186 168 L 159 159 L 186 150 Z" fill="#EC4899" />
          <rect x="192" y="229" width="16" height="120" rx="8" fill="white" />
          <path d="M 200 303 C 178 299, 136 279, 116 253" stroke="white" strokeWidth="16" fill="none" strokeLinecap="round" />
          <path d="M 200 303 C 222 299, 264 279, 284 253" stroke="white" strokeWidth="16" fill="none" strokeLinecap="round" />
        </svg>
        <h1 className="text-5xl font-bold bg-gradient-to-br from-[#FF8C69] to-[#FF6B9D] bg-clip-text text-transparent tracking-tight">
          Romy
        </h1>
        <p className="text-sm text-gray-400 dark:text-gray-500 italic -mt-1 mb-1">roe-mee</p>
        <p className="text-lg text-gray-600 dark:text-gray-400">Your AI Health Companion</p>
      </div>

      {mode === 'welcome' && (
        <div className="w-full max-w-sm space-y-4">
          {isIOS && (
            <button
              onClick={handleAppleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center space-x-3 px-4 py-3.5 bg-black dark:bg-white text-white dark:text-black border-2 border-black dark:border-white rounded-xl hover:bg-gray-900 dark:hover:bg-gray-100 transition-all disabled:opacity-50 shadow-sm"
            >
              <AppleLogo size={22} weight="fill" />
              <span className="font-semibold">Continue with Apple</span>
            </button>
          )}

          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center space-x-3 px-4 py-3.5 bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all disabled:opacity-50 shadow-sm"
          >
            <GoogleLogo size={22} className="text-gray-700 dark:text-gray-300" />
            <span className="font-semibold text-gray-700 dark:text-gray-300">Continue with Google</span>
          </button>

          <button
            onClick={() => setMode('signup')}
            className="w-full px-4 py-3.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg"
          >
            Create Account
          </button>

          <button
            onClick={() => setMode('signin')}
            className="w-full px-4 py-3 text-gray-600 dark:text-gray-400 font-medium text-sm"
          >
            Already have an account? <span className="text-pink-600 dark:text-pink-400">Sign in</span>
          </button>
        </div>
      )}

      {(mode === 'signin' || mode === 'signup') && (
        <div className="w-full max-w-sm">
          {/* Inline legal line — sign-up only, covers all auth methods */}
          {isSignUp && (
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4 text-center">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="text-pink-600 dark:text-pink-400 underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-pink-600 dark:text-pink-400 underline">Privacy Policy</Link>
              .
            </p>
          )}

          {/* Apple button (iOS only) */}
          {isIOS && (
            <button
              onClick={handleAppleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center space-x-3 px-4 py-3 bg-black dark:bg-white text-white dark:text-black border-2 border-black dark:border-white rounded-xl hover:bg-gray-900 dark:hover:bg-gray-100 transition-all disabled:opacity-50 shadow-sm mb-3"
            >
              <AppleLogo size={20} weight="fill" />
              <span className="font-medium">Continue with Apple</span>
            </button>
          )}

          {/* Google button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center space-x-3 px-4 py-3 bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all disabled:opacity-50 shadow-sm mb-4"
          >
            <GoogleLogo size={20} className="text-gray-700 dark:text-gray-300" />
            <span className="font-medium text-gray-700 dark:text-gray-300">Continue with Google</span>
          </button>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-700" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gradient-to-b from-orange-50 to-pink-50 dark:from-gray-900 dark:to-gray-900 text-gray-500 dark:text-gray-400">or</span>
            </div>
          </div>

          {/* Email form */}
          <form onSubmit={handleEmailAuth} className="space-y-3">
            {isSignUp && (
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <Input
                  inputSize="lg"
                  className="w-full pl-10 pr-4 rounded-xl"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="First name"
                  required
                />
              </div>
            )}

            <div className="relative">
              <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <Input
                inputSize="lg"
                className="w-full pl-10 pr-4 rounded-xl"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
              />
            </div>

            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <Input
                inputSize="lg"
                className="w-full pl-10 pr-4 rounded-xl"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                minLength={6}
              />
            </div>

            {!isSignUp && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={async () => {
                    if (!email) { setError('Enter your email first'); return; }
                    try {
                      await resetPassword(email);
                      setResetSent(true);
                      setError('');
                    } catch { setError('Could not send reset email. Check your address.'); }
                  }}
                  className="text-xs text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {resetSent && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl text-sm text-green-600">
                Password reset email sent! Check your inbox.
              </div>
            )}

            {error && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl text-sm text-amber-800 dark:text-amber-200">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-pink-600 transition-all disabled:opacity-50"
            >
              {loading ? 'Loading...' : isSignUp ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          {/* Toggle mode */}
          <div className="mt-5 text-center">
            <button
              onClick={() => {
                setMode(isSignUp ? 'signin' : 'signup');
                setError('');
              }}
              className="text-sm text-pink-600 dark:text-pink-400 font-medium"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
        </div>
      )}

      {/* Footer links */}
      <div className="mt-10 flex space-x-4 text-xs text-gray-400 dark:text-gray-500">
        <Link to="/terms" className="hover:text-gray-600 dark:hover:text-gray-300">Terms</Link>
        <Link to="/privacy" className="hover:text-gray-600 dark:hover:text-gray-300">Privacy</Link>
      </div>
    </div>
  );
}
